"use client"

import React, { createContext, useContext, useEffect, useState } from "react"
import { useAuthContext } from "./auth-context"
import { usePathname } from "next/navigation"
import { configuratorTypesAPI } from "@/lib/api"

interface ConfiguratorType {
  id: string
  name: string
  description?: string
  icon?: string
  is_active?: boolean
  admin_enabled?: boolean
  user_enabled?: boolean
}

interface ConfiguratorTypesContextType {
  configuratorTypes: ConfiguratorType[]
  loading: boolean
  error: string | null
  getTypeById: (id: string) => ConfiguratorType | undefined
  getTypeByName: (name: string) => ConfiguratorType | undefined
}

const ConfiguratorTypesContext = createContext<ConfiguratorTypesContextType | undefined>(undefined)

export function ConfiguratorTypesProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const { configuratorTypes: cachedTypes } = useAuthContext()
  const [configuratorTypes, setConfiguratorTypes] = useState<ConfiguratorType[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchConfiguratorTypes = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await configuratorTypesAPI.getConfiguratorTypes()
      setConfiguratorTypes(response.configuratorTypes)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || "Failed to load configurator types"
      setError(errorMessage)
      console.error("Error fetching configurator types:", err)
    } finally {
      setLoading(false)
    }
  }

  const getTypeById = (id: string): ConfiguratorType | undefined => {
    return configuratorTypes.find(type => type.id === id)
  }

  const getTypeByName = (name: string): ConfiguratorType | undefined => {
    return configuratorTypes.find(type => type.name === name)
  }

  useEffect(() => {
    // If we already have cached types from AuthContext, use them and skip fetching
    if (cachedTypes && cachedTypes.length > 0) {
      setConfiguratorTypes(cachedTypes)
      setLoading(false)
      return
    }

    const unauthenticatedPaths = ["/login", "/reset-password", "/register"]
    const isUnauthPath = unauthenticatedPaths.some(path => pathname.startsWith(path))
    if (isUnauthPath) {
      // Skip fetching to avoid 401 errors when user is not authenticated
      setLoading(false)
      return
    }

    fetchConfiguratorTypes()
  }, [pathname, cachedTypes])

  return (
    <ConfiguratorTypesContext.Provider
      value={{
        configuratorTypes,
        loading,
        error,
        getTypeById,
        getTypeByName,
      }}
    >
      {children}
    </ConfiguratorTypesContext.Provider>
  )
}

export function useConfiguratorTypesContext() {
  const context = useContext(ConfiguratorTypesContext)
  if (context === undefined) {
    throw new Error("useConfiguratorTypesContext must be used within a ConfiguratorTypesProvider")
  }
  return context
} 